import * as THREE from "three";

/**
 * A cosy living room — the final scene where the deepfake call comes in. Zero
 * external assets. A small couch + coffee table with a phone on it; warm lamp
 * light and a window with evening light. Waypoints:
 *   - wp_living_establish: wide, warm room (hard CUT in from the hacker room).
 *   - wp_living_phone: push in on the phone on the coffee table (~[0,0.46,0.7]),
 *     where it rings and the DeepfakeCall overlay takes over.
 */
export function LivingRoom() {
  return (
    <group>
      {/* wood floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[22, 22]} />
        <meshStandardMaterial color="#9c7b54" roughness={0.95} />
      </mesh>

      {/* rug under the couch. Raised clear of the floor (0) AND the contact-shadow
          plane, with polygonOffset, so the near-coplanar planes don't z-fight and
          shimmer as the camera moves (the "flickering couch/carpet" bug -- see
          Office.tsx for the same fix on the ransomware-office bank floor). */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0.4]} receiveShadow>
        <planeGeometry args={[4.6, 3.2]} />
        <meshStandardMaterial
          color="#b7c2c0"
          roughness={1}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>

      {/* walls */}
      <mesh position={[0, 2.2, -2.6]} receiveShadow>
        <boxGeometry args={[18, 4.4, 0.2]} />
        <meshStandardMaterial color="#e3ddd2" roughness={0.97} />
      </mesh>
      <mesh position={[-4.4, 2.2, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[10, 4.4, 0.2]} />
        <meshStandardMaterial color="#d9d2c6" roughness={0.97} />
      </mesh>

      <Window />
      <Couch />
      <CoffeeTable />
      <FloorLamp />
      <WallArt />
      <Rubber />

      {/* warm lamp pool + soft ambient */}
      <pointLight position={[2.4, 1.7, 0.6]} intensity={1.8} distance={6} color="#ffd9a0" />
      <pointLight position={[0, 3.0, 1.5]} intensity={0.4} distance={10} color="#fff0d8" />
      <directionalLight position={[-3, 2.4, -1]} intensity={0.3} color="#bcd0e8" />
    </group>
  );
}

function Window() {
  return (
    <group position={[-2.6, 1.9, -2.48]}>
      <mesh>
        <planeGeometry args={[2.0, 1.6]} />
        <meshStandardMaterial
          color="#2a3a52"
          emissive={new THREE.Color("#37506e")}
          emissiveIntensity={0.5}
          roughness={0.2}
          metalness={0.3}
        />
      </mesh>
      {/* mullions */}
      <mesh position={[0, 0, 0.02]}>
        <planeGeometry args={[0.05, 1.6]} />
        <meshStandardMaterial color="#cfc6b6" />
      </mesh>
      <mesh position={[0, 0, 0.02]}>
        <planeGeometry args={[2.0, 0.05]} />
        <meshStandardMaterial color="#cfc6b6" />
      </mesh>
      {/* frame */}
      <mesh position={[0, 0, -0.02]}>
        <boxGeometry args={[2.2, 1.8, 0.08]} />
        <meshStandardMaterial color="#c8bfaf" roughness={0.9} />
      </mesh>
    </group>
  );
}

function Couch() {
  const fabric = "#5f6f8a";
  return (
    <group position={[0, 0, -1.4]}>
      {/* seat base */}
      <mesh position={[0, 0.34, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.0, 0.28, 0.9]} />
        <meshStandardMaterial color={fabric} roughness={0.95} />
      </mesh>
      {/* backrest */}
      <mesh position={[0, 0.66, -0.36]} castShadow>
        <boxGeometry args={[2.0, 0.5, 0.22]} />
        <meshStandardMaterial color={fabric} roughness={0.95} />
      </mesh>
      {/* arms */}
      {[-1.0, 1.0].map((x, i) => (
        <mesh key={i} position={[x, 0.48, 0]} castShadow>
          <boxGeometry args={[0.22, 0.4, 0.9]} />
          <meshStandardMaterial color="#54627a" roughness={0.95} />
        </mesh>
      ))}
      {/* seat cushions */}
      {[-0.5, 0.5].map((x, i) => (
        <mesh key={i} position={[x, 0.5, 0.04]} castShadow>
          <boxGeometry args={[0.9, 0.12, 0.82]} />
          <meshStandardMaterial color="#6b7c98" roughness={0.9} />
        </mesh>
      ))}
      {/* throw cushions */}
      {[-0.6, 0.6].map((x, i) => (
        <mesh key={i} position={[x, 0.66, -0.2]} rotation={[0, 0, 0.2 * (i ? 1 : -1)]} castShadow>
          <boxGeometry args={[0.34, 0.34, 0.12]} />
          <meshStandardMaterial color={i ? "#c2724f" : "#d8b86a"} roughness={0.95} />
        </mesh>
      ))}
      {/* feet */}
      {[
        [-0.9, 0.42],
        [0.9, 0.42],
        [-0.9, -0.42],
        [0.9, -0.42],
      ].map((p, i) => (
        <mesh key={i} position={[p[0], 0.1, p[1]]} castShadow>
          <cylinderGeometry args={[0.04, 0.04, 0.2, 10]} />
          <meshStandardMaterial color="#3a2a1c" roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}

/** Coffee table with the phone on it (the ringing phone in the final beat). */
function CoffeeTable() {
  return (
    <group position={[0, 0, 0.7]}>
      {/* top */}
      <mesh position={[0, 0.42, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 0.06, 0.62]} />
        <meshStandardMaterial color="#6b4a2e" roughness={0.5} metalness={0.1} />
      </mesh>
      {[
        [-0.52, 0.24],
        [0.52, 0.24],
        [-0.52, -0.24],
        [0.52, -0.24],
      ].map((p, i) => (
        <mesh key={i} position={[p[0], 0.2, p[1]]} castShadow>
          <boxGeometry args={[0.06, 0.4, 0.06]} />
          <meshStandardMaterial color="#4a3320" roughness={0.6} />
        </mesh>
      ))}
      {/* the phone (screen up, lying flat on the table) */}
      <group position={[0, 0.46, 0.0]} rotation={[0, 0.2, 0]}>
        <mesh castShadow>
          <boxGeometry args={[0.1, 0.012, 0.2]} />
          <meshStandardMaterial color="#0a0a0c" roughness={0.4} metalness={0.3} />
        </mesh>
        <mesh position={[0, 0.0072, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.088, 0.182]} />
          <meshStandardMaterial
            color="#0c1018"
            emissive={new THREE.Color("#2f6fa8")}
            emissiveIntensity={0.7}
          />
        </mesh>
      </group>
      {/* a book + mug */}
      <mesh position={[0.36, 0.47, 0.1]} rotation={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[0.2, 0.03, 0.28]} />
        <meshStandardMaterial color="#9c4f43" roughness={0.9} />
      </mesh>
      <mesh position={[-0.36, 0.49, -0.1]} castShadow>
        <cylinderGeometry args={[0.045, 0.04, 0.08, 16]} />
        <meshStandardMaterial color="#e7e2d8" roughness={0.6} />
      </mesh>
    </group>
  );
}

function FloorLamp() {
  return (
    <group position={[2.4, 0, -0.4]}>
      <mesh position={[0, 0.02, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.2, 0.04, 16]} />
        <meshStandardMaterial color="#3a3a3a" metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.8, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 1.6, 10]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[0, 1.62, 0]} castShadow>
        <cylinderGeometry args={[0.22, 0.28, 0.34, 20, 1, true]} />
        <meshStandardMaterial
          color="#f4e3c2"
          emissive={new THREE.Color("#ffce8a")}
          emissiveIntensity={0.7}
          roughness={0.8}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

function WallArt() {
  return (
    <group position={[1.6, 2.0, -2.48]}>
      <mesh>
        <boxGeometry args={[1.0, 0.7, 0.04]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0, 0.025]}>
        <planeGeometry args={[0.88, 0.58]} />
        <meshStandardMaterial color="#8aa6b8" roughness={0.7} />
      </mesh>
    </group>
  );
}

/** A small potted plant in the corner. */
function Rubber() {
  return (
    <group position={[-3.6, 0, 1.4]}>
      <mesh position={[0, 0.22, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.14, 0.44, 16]} />
        <meshStandardMaterial color="#b6794a" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.7, 0]} castShadow>
        <icosahedronGeometry args={[0.34, 1]} />
        <meshStandardMaterial color="#3f7a4f" roughness={1} flatShading />
      </mesh>
      <mesh position={[0.16, 0.92, 0.05]} castShadow>
        <icosahedronGeometry args={[0.22, 1]} />
        <meshStandardMaterial color="#46885a" roughness={1} flatShading />
      </mesh>
    </group>
  );
}
